import { useRef, useState } from 'react'
import { apiBase, webTokenStorage } from '../../lib/api.ts'
import { hexDoPapel, PAPEIS, paletaVazia, type Paleta, type Papel } from './paleta'
import './marca.css'

/**
 * Painel "Cores da empresa" do Escritório (SPEC-20261011-1733): o admin escolhe as cores da marca
 * e onde cada uma entra (paredes das salas, estofados, tapetes, recepção e letreiro), vê no
 * tabuleiro na hora e salva para o workspace todo. A logo vai na recepção e no anel do chão.
 */

export type MarcaDoEscritorio = { paleta: Paleta | null; logo: string | null; logoPropria: boolean }

const NOMES: Record<Papel, { titulo: string; dica: string }> = {
  paredes: { titulo: 'Paredes das salas', dica: 'Fica sempre num tom claro da cor' },
  estofados: { titulo: 'Sofás e cadeiras', dica: 'Estofados de todas as salas' },
  tapetes: { titulo: 'Tapetes e carpetes', dica: 'Chão das salas e tapetes' },
  marca: { titulo: 'Recepção e letreiro', dica: 'A cor mais forte da marca' },
}
const SUGESTOES = ['#6b2fd6', '#ffc21a', '#1c1c24', '#e8455a', '#1aa37a', '#2f6fe0']
const MAX_CORES = 4

function coresDaPaleta(p: Paleta | null) {
  const lista: string[] = []
  for (const k of PAPEIS) {
    const c = p?.[k]
    if (c && !lista.includes(c)) lista.push(c)
  }
  return lista
}

/** Claridade aproximada (0..1) de um hex, para não pôr cor escura em área grande. */
function claridade(hex: string) {
  const n = parseInt(hex.slice(1), 16)
  return (0.2126 * ((n >> 16) & 255) + 0.7152 * ((n >> 8) & 255) + 0.0722 * (n & 255)) / 255
}

/**
 * Distribui as cores da marca pelos papéis: a 1ª vai nas paredes e na recepção, a 2ª nos
 * estofados, a 3ª nos tapetes. Cor quase preta não vai para área grande (preto é detalhe):
 * se houver outra, ela ocupa o lugar.
 */
function distribuir(cores: string[]): Paleta {
  if (!cores.length) return {}
  const claras = cores.filter((c) => claridade(c) > 0.22)
  const fila = claras.length ? claras : cores
  const [a, b, c] = fila as [string, string | undefined, string | undefined]
  return { paredes: a, estofados: b ?? a, tapetes: c ?? a, marca: cores[0] as string }
}

type Props = {
  workspaceId: string
  marca: MarcaDoEscritorio
  /** Prévia ao vivo no tabuleiro. */
  onPrevia: (p: Paleta | null) => void
  onSalvar: (p: Paleta) => Promise<void>
  onLogo: (m: MarcaDoEscritorio) => void
  onFechar: () => void
}

export function PainelDaMarca({ marca, onPrevia, onSalvar, onLogo, onFechar }: Props) {
  const [cores, setCores] = useState<string[]>(() => (marca.paleta?.cores?.length ? marca.paleta.cores : coresDaPaleta(marca.paleta)))
  const [paleta, setPaleta] = useState<Paleta>(() => marca.paleta ?? {})
  const [salvando, setSalvando] = useState(false)
  const [aviso, setAviso] = useState<string | null>(null)
  const arquivo = useRef<HTMLInputElement>(null)
  const mudou = JSON.stringify(paleta) !== JSON.stringify(marca.paleta ?? {})

  const aplicar = (p: Paleta) => {
    setPaleta(p)
    onPrevia(paletaVazia(p) ? null : p)
  }
  const trocarCores = (lista: string[], redistribuir: boolean) => {
    setCores(lista)
    if (redistribuir) aplicar({ ...distribuir(lista), cores: lista })
    else {
      // papel que usava uma cor que mudou passa a usar a nova
      const p: Paleta = {}
      for (const k of PAPEIS) {
        const i = cores.indexOf(paleta[k] ?? '')
        p[k] = i >= 0 ? (lista[i] ?? null) : null
      }
      aplicar({ ...p, cores: lista })
    }
  }

  const enviarLogo = async (f: File) => {
    setAviso(null)
    if (f.size > 1_000_000) return setAviso('A logo precisa ter até 1 MB.')
    const form = new FormData()
    form.append('file', f)
    const r = await fetch(`${apiBase()}/office/logo`, { method: 'POST', headers: { authorization: `Bearer ${webTokenStorage.get() ?? ''}` }, body: form }).catch(() => null)
    if (!r?.ok) return setAviso('Não deu para enviar a logo. Tente uma imagem PNG ou JPG.')
    onLogo(((await r.json()) as { marca: MarcaDoEscritorio }).marca)
  }
  const tirarLogo = async () => {
    const r = await fetch(`${apiBase()}/office/logo`, { method: 'DELETE', headers: { authorization: `Bearer ${webTokenStorage.get() ?? ''}` } }).catch(() => null)
    if (r?.ok) onLogo(((await r.json()) as { marca: MarcaDoEscritorio }).marca)
  }

  const salvar = async () => {
    setSalvando(true)
    setAviso(null)
    try {
      await onSalvar(paleta)
    } catch {
      setAviso('Não deu para salvar. Tente de novo.')
    } finally {
      setSalvando(false)
    }
  }

  return (
    <aside className="escm" aria-label="Cores da empresa">
      <header className="escm-topo">
        <div>
          <h2>Cores da empresa</h2>
          <p>Vale para todos os andares. Luz e sombra continuam as do escritório.</p>
        </div>
        <button type="button" className="escm-x" aria-label="Fechar" onClick={onFechar}>
          <i className="fas fa-times" aria-hidden />
        </button>
      </header>

      <section className="escm-logo">
        <div className="escm-logo-quadro">{marca.logo ? <img src={marca.logo} alt="Logo da empresa" /> : <i className="fas fa-image" aria-hidden />}</div>
        <div className="escm-logo-txt">
          <b>Logo</b>
          <span>{marca.logo ? (marca.logoPropria ? 'Aparece na recepção e no anel do chão.' : 'Usando a logo do workspace.') : 'Sem logo: a recepção fica só com a cor.'}</span>
          <span className="escm-logo-botoes">
            <button type="button" className="escm-link" onClick={() => arquivo.current?.click()}>
              {marca.logoPropria ? 'Trocar logo' : 'Enviar logo'}
            </button>
            {marca.logoPropria ? (
              <button type="button" className="escm-link escm-link--fraco" onClick={() => void tirarLogo()}>
                Tirar
              </button>
            ) : null}
          </span>
          <input
            ref={arquivo}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/svg+xml"
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0]
              e.target.value = ''
              if (f) void enviarLogo(f)
            }}
          />
        </div>
      </section>

      <section className="escm-bloco">
        <h3>Cores da marca</h3>
        <div className="escm-cores">
          {cores.map((c, i) => (
            <span key={i} className="escm-cor">
              <label className="escm-cor-amostra" style={{ background: c }} title="Trocar a cor">
                <input type="color" value={c} onChange={(e) => trocarCores(cores.map((x, j) => (j === i ? e.target.value : x)), false)} />
              </label>
              <button type="button" className="escm-cor-tirar" aria-label={`Tirar a cor ${c}`} onClick={() => trocarCores(cores.filter((_, j) => j !== i), true)}>
                <i className="fas fa-times" aria-hidden />
              </button>
            </span>
          ))}
          {cores.length < MAX_CORES ? (
            <label className="escm-cor-nova" title="Adicionar uma cor">
              <i className="fas fa-plus" aria-hidden />
              <input type="color" value={SUGESTOES[cores.length] ?? '#888888'} onChange={(e) => trocarCores([...cores, e.target.value], true)} />
            </label>
          ) : null}
        </div>
        {!cores.length ? (
          <div className="escm-sugestoes">
            <span>Ou comece com:</span>
            {[
              ['#6b2fd6', '#ffc21a', '#1c1c24'],
              ['#e8455a', '#2b2d42', '#f2e9e4'],
              ['#1aa37a', '#f4b942', '#24324a'],
            ].map((trio) => (
              <button key={trio.join()} type="button" className="escm-trio" onClick={() => trocarCores(trio, true)} aria-label={`Usar ${trio.join(', ')}`}>
                {trio.map((c) => (
                  <i key={c} style={{ background: c }} />
                ))}
              </button>
            ))}
          </div>
        ) : null}
      </section>

      {cores.length ? (
        <section className="escm-bloco">
          <h3>Onde cada cor entra</h3>
          <ul className="escm-papeis">
            {PAPEIS.map((k) => {
              const atual = paleta[k] ?? null
              return (
                <li key={k}>
                  <span className="escm-tom" style={{ background: atual ? hexDoPapel(k, atual) : 'transparent' }} data-original={!atual || undefined} aria-hidden />
                  <span className="escm-papel">
                    <b>{NOMES[k].titulo}</b>
                    <small>{NOMES[k].dica}</small>
                  </span>
                  <span className="escm-escolha" role="radiogroup" aria-label={NOMES[k].titulo}>
                    {cores.map((c) => (
                      <button key={c} type="button" role="radio" aria-checked={atual === c} className="escm-bolinha" style={{ background: c }} aria-label={c} onClick={() => aplicar({ ...paleta, [k]: c, cores })} />
                    ))}
                    <button type="button" role="radio" aria-checked={!atual} className="escm-bolinha escm-bolinha--original" aria-label="Cor original" title="Cor original" onClick={() => aplicar({ ...paleta, [k]: null, cores })} />
                  </span>
                </li>
              )
            })}
          </ul>
        </section>
      ) : null}

      {aviso ? <p className="escm-aviso" role="alert">{aviso}</p> : null}

      <footer className="escm-pe">
        <button
          type="button"
          className="escm-link escm-link--fraco"
          disabled={paletaVazia(paleta) && !cores.length}
          onClick={() => {
            setCores([])
            aplicar({})
          }}
        >
          Voltar ao original
        </button>
        <button type="button" className="escm-salvar" disabled={!mudou || salvando} onClick={() => void salvar()}>
          {salvando ? 'Salvando…' : mudou ? 'Salvar cores' : 'Salvo'}
        </button>
      </footer>
    </aside>
  )
}
